import { ContextManager, OnQuery } from 'dreamstate';

import { Optional } from '@vimeo/core/types/generic';
import { Logger } from '@vimeo/logging';

import { liveApplicationConfig } from '../../configs/LiveApplicationConfig';
import { liveMediaConfig } from '../../configs/LiveMediaConfig';
import { EDependencyQuery } from '../../store/queries';
import { EDependencySignal, ELiveSignal } from '../../store/signals';
import { TAgoraSDK } from '../../types/agora';
import { EExternalModule } from '../../types/composer_session';
import { IFirebaseDependencies } from '../../types/firebase';
import { IStoreInitialState } from '../../types/store';
import { applyAgoraParameter } from '../../utils/agora/agora_media';
import { firebaseSDKLog } from '../../utils/firebase/logging';
import { ELiveErrorCategory, trackLiveAction, trackLiveError } from '../../utils/tracking';

/**
 * Service that manages lazy dependencies and emits global signals.
 * Adds opportunity to manage dynamic data in a dynamic way.
 */
export class ModuleManager extends ContextManager {
  public firebase: Optional<IFirebaseDependencies> = null;
  public agora: Optional<TAgoraSDK> = null;

  private readonly log: Logger = new Logger('💫MM');
  private readonly externalModules: Array<EExternalModule> = [];

  public constructor(initialState?: IStoreInitialState) {
    super();

    if (initialState?.externalModules) {
      this.externalModules = initialState.externalModules;
    } else {
      this.log.warn('Dependency manager probably do not needed in your app');
    }
  }

  public loadDependencies(): void {
    this.externalModules.forEach((module) => {
      switch (module) {
        case EExternalModule.FIREBASE:
          this.loadFirebase();
          break;
        case EExternalModule.AGORA:
          this.loadAgoraSdk();
          break;
        case EExternalModule.FIRESTORE:
          break;
        default:
          this.log.warn('Passed incorrect dependency module');
      }
    });
  }

  public override onProvisionStarted(): void {
    this.loadDependencies();
  }

  /**
   * Lazy load for firebase sdk separate modules.
   */
  public async loadFirebase(): Promise<void> {
    try {
      this.log.info('Firebase chunks loading initialized');
      const hasFirestore = this.externalModules.includes(EExternalModule.FIRESTORE);

      const [firebase, auth, database, storage, firestore] = await Promise.all([
        import('firebase/app'),
        import('firebase/auth'),
        import('firebase/database'),
        import('firebase/storage'),
        hasFirestore ? import('firebase/firestore') : null,
      ]);

      this.firebase = { firebase, auth, database, storage, firestore };

      trackLiveAction('firebase_module_loaded');
      this.log.info('Firebase chunks successfully loaded');

      this.configureFirebaseSDK(this.firebase);

      if (!this.IS_DISPOSED) {
        this.emitSignal({
          type: EDependencySignal.FIREBASE_DEPENDENCIES_LOADED,
          data: this.firebase,
        });
      }
    } catch (error) {
      this.log.error('Failed to load firebase dependencies:', error);
      this.emitSignal({ type: ELiveSignal.GLOBAL_ERROR, data: error });

      trackLiveError(error, {
        method: 'loadFirebase',
        category: ELiveErrorCategory.GENERIC,
      });
    }
  }

  /**
   * Lazy load for agora sdk.
   */
  public async loadAgoraSdk(): Promise<void> {
    try {
      this.log.info('Agora chunks loading initialized');

      const [agoraSDK, VirtualBackgroundExtension] = await Promise.all([
        import('agora-rtc-sdk-ng').then((module) => {
          this.configureAgoraSDK(module.default);

          return module.default;
        }),
        import('agora-extension-virtual-background').then((module) => module.default),
      ]);

      this.agora = agoraSDK;

      trackLiveAction('agora_module_loaded');
      this.log.info('Agora chunks successfully loaded');

      if (!this.IS_DISPOSED) {
        this.emitSignal({
          type: EDependencySignal.AGORA_DEPENDENCIES_LOADED,
          data: {
            agoraSDK,
            VirtualBackgroundExtension,
          },
        });
      }
    } catch (error) {
      this.log.error('Failed to load agora dependencies:', error);
      this.emitSignal({ type: ELiveSignal.GLOBAL_ERROR, data: error });

      trackLiveError(error, {
        method: 'loadAgoraSdk',
        category: ELiveErrorCategory.GENERIC,
      });
    }
  }

  /**
   * Configure global variables for agora SDK dependency and use it instantly on load.
   */
  private configureAgoraSDK(agoraSDK: TAgoraSDK): void {
    this.log.info('Agora log level:', liveMediaConfig.AGORA.LOG_LEVEL);
    // Force agora SDK to shut up or it will spam everything down.
    agoraSDK.setLogLevel(liveMediaConfig.AGORA.LOG_LEVEL);
    applyAgoraParameter(agoraSDK, 'DISABLE_WEBAUDIO', liveMediaConfig.AGORA.DISABLE_WEB_AUDIO);

    if (liveMediaConfig.AGORA.LOG_UPLOAD) {
      this.log.info('Enabling agora log uploading');
      agoraSDK.enableLogUpload();
    }
  }

  /**
   * Configure firebase SDK on module load.
   */
  private configureFirebaseSDK(firebaseSDK: IFirebaseDependencies): void {
    if (liveApplicationConfig.TRACKING.IS_FIREBASE_SDK_LOG_ENABLED) {
      this.log.info('Enabling firebase SDK logs');
      firebaseSDK.database.enableLogging((message) => firebaseSDKLog.info('[SDK]', message));
      firebaseSDK.firebase.setLogLevel('verbose');
    }
  }

  @OnQuery(EDependencyQuery.AGORA_DEPENDENCIES)
  private onAgoraDependenciesRequired(): Optional<TAgoraSDK> {
    return this.agora;
  }

  @OnQuery(EDependencyQuery.FIREBASE_DEPENDENCIES)
  private onFirebaseDependenciesRequired(): Optional<IFirebaseDependencies> {
    return this.firebase;
  }
}
